import { Component, ContentChildren, QueryList, AfterContentInit, EventEmitter, Output, Input } from '@angular/core';
import { WizardStepComponent } from './wizard-step.component';
import { CommonModule } from '@angular/common';
import { LoadingService } from '../../services/loading.service';
import { LoadingHelper } from '../../services/loading-helper';

@Component({
    selector: 'wizard-control',
    imports: [CommonModule],
    templateUrl: './wizard-control.component.html',
    styleUrls: ['./wizard-control.component.scss']
})
export class WizardControlComponent implements AfterContentInit {
    @ContentChildren(WizardStepComponent)
    steps: QueryList<WizardStepComponent> = new QueryList<WizardStepComponent>();

    @Output()
    onFinished = new EventEmitter();

    @Input()
    activeTabIndex: number | undefined;

    public isFinishing: boolean = false;

    constructor(private loadingService: LoadingService) {}

    ngAfterContentInit() {
        const isRedirected = sessionStorage.getItem('isRedirected');
        const lastActiveStepIndex = localStorage.getItem('lastActiveStepIndex');
        const stepsArray = this.steps.toArray();

        // If activeTabIndex is provided, activate that step
        if (typeof this.activeTabIndex === 'number' && stepsArray[this.activeTabIndex]) {
            this.deactiveateAllSteps();
            
            // Mark all steps before the active step as completed
            for (let i = 0; i < this.activeTabIndex; i++) {
                stepsArray[i].completed = true;
            }
            
            stepsArray[this.activeTabIndex].onActivate(this.activeTabIndex + 1);
        }
        else if (isRedirected) {
            sessionStorage.removeItem('isRedirected');

            var index = this.getStepIndex(this.steps.first);

            this.deactiveateAllSteps();
            this.steps.first.onActivate(index);
        }
        else if (lastActiveStepIndex !== null && stepsArray[+lastActiveStepIndex]) {
            this.deactiveateAllSteps();

            // Mark all steps before the reactivated step as completed
            for (let i = 0; i < +lastActiveStepIndex; i++) {
                stepsArray[i].completed = true;
            }

            stepsArray[+lastActiveStepIndex].onActivate(+lastActiveStepIndex);
        }
        else {
            const activeStep = this.getActiveStep();

            if (!activeStep) {
                var index = this.getStepIndex(this.steps.first);
                this.steps.first.onActivate(index);
            }
        }
    }

    getStepIndex(step: WizardStepComponent) {
        return this.steps.toArray().indexOf(step) + 1;
    }

    getActiveStep() {
        return this.steps.find(step => step.active);
    }

    isNextDisabled() {
        return !this.steps.find(step => step.active)?.valid;
    }

    isFirstStep() {
        return this.getStepIndex(this.steps.find(step => step.active)!) === 1;
    }

    isLastStep() {
        return this.getStepIndex(this.steps.find(step => step.active)!) === this.steps.length;
    }

    isDisallowingPrevious() {
        return !this.steps.find(step => step.active)?.allowPrevious;
    }

    deactiveateAllSteps() {
        this.steps
            .toArray()
            .forEach(s => s.active = false);
    }

    marksSubsequentStepsAsIncomplete(nextStepIndex: number) {
        for (let i = nextStepIndex; i < this.steps.length; i++) {
            this.steps.toArray()[i].completed = false;
        }
    }

    selectStep(nextStep: WizardStepComponent) {
        const activeStep = this.getActiveStep();

        const nextStepIndex = this.getStepIndex(nextStep);
        const activeStepIndex = activeStep
            ? this.getStepIndex(activeStep)
            : 0;

        if (nextStepIndex === activeStepIndex + 1 && activeStep?.valid) {
            this.deactiveateAllSteps();

            nextStep.onActivate(nextStepIndex);
            activeStep.completed = true;
        }
        else if (nextStepIndex <= activeStepIndex && activeStep?.allowPrevious) {
            this.deactiveateAllSteps();
            this.marksSubsequentStepsAsIncomplete(nextStepIndex);

            nextStep.onActivate(nextStepIndex);
            nextStep.completed = false;
        }

        // Save the index of the active step to localStorage
        localStorage.setItem('lastActiveStepIndex', (nextStepIndex - 1).toString());
    }

    async nextStep() {
        const activeStep = this.getActiveStep();

        if (activeStep) {
            LoadingHelper.withLoading(
                activeStep.onNext(),
                this.loadingService
            ).subscribe({
                next: (result) => {
                    if (result) {
                        activeStep.completed = true;

                        const nextStep = this.steps.toArray()[this.getStepIndex(activeStep)];

                        if (nextStep) {
                            this.selectStep(nextStep);
                        }
                    }
                    else {
                        console.warn('Step validation failed. Cannot proceed to the next step.');
                    }
                },
                error: (error) => {
                    console.error('Error during next step:', error);
                }
            });
        }
    }

    async previousStep() {
        const activeStep = this.getActiveStep();

        if (activeStep) {
            const prevStep = this.steps.toArray()[this.getStepIndex(activeStep) - 2];

            if (prevStep && prevStep.allowPrevious) {
                LoadingHelper.withLoading(
                    activeStep.onPrevious(),
                    this.loadingService
                ).subscribe({
                    next: (result) => {
                        if (result) {
                            activeStep.completed = false;
                            prevStep.completed = false;

                            this.selectStep(prevStep);
                        }
                        else {
                            console.warn('Step validation failed. Cannot move to the previous step.');
                        }
                    },
                    error: (error) => {
                        console.error('Error during previous step:', error);
                    }
                });
            }
        }
    }

    async finish() {
        if (this.isFinishing) {
            // Prevents double-clicks
            return;
        }

        this.isFinishing = true;
        this.onFinished.emit();
    }

    /**
     * Reset the finishing state - call this after the finish operation completes
     */
    resetFinishing(): void {
        this.isFinishing = false;
    }
}
